import React, { useEffect, useState } from 'react';
import {
  Card, Row, Col, Tag, Typography, Descriptions, Progress, Spin, Button, Space, Divider, Avatar, Switch, Input, Modal, message, Alert
} from 'antd';
import {
  ArrowLeftOutlined, EditOutlined, CalendarOutlined, UserOutlined, TeamOutlined, FolderOutlined, CustomerServiceOutlined, CopyOutlined, ReloadOutlined, PlusOutlined, LinkOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const ProjectView = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [members, setMembers] = useState([]);
  const [taskStatuses, setTaskStatuses] = useState([]);

  // Support settings state
  const [supportLoading, setSupportLoading] = useState(false);
  const [newComponent, setNewComponent] = useState('');
  const [componentModalVisible, setComponentModalVisible] = useState(false);

  useEffect(() => {
    if (id) {
      fetchProjectData();
    }
  }, [id]);

  const fetchProjectData = async () => {
    try {
      setLoading(true);
      const [projectRes, membersRes, statusesRes] = await Promise.all([
        axios.get(`/project_board/projects/${id}/`),
        axios.get(`/project_board/members/?project=${id}`),
        axios.get(`/project_board/statuses/?project=${id}`)
      ]);

      setProject(projectRes.data);
      setMembers(membersRes.data.results || membersRes.data);
      setTaskStatuses(statusesRes.data.results || statusesRes.data);
    } catch (error) {
      console.error('Error fetching project:', error);
    } finally {
      setLoading(false);
    }
  };

  // Support settings functions
  const handleEnableSupport = async (enabled) => {
    setSupportLoading(true);
    try {
      const endpoint = enabled ? 'enable-support' : 'disable-support';
      await axios.post(`/project_board/projects/${id}/${endpoint}/`);
      message.success(`Support ${enabled ? 'enabled' : 'disabled'} successfully`);
      fetchProjectData();
    } catch (error) {
      message.error('Failed to update support settings');
    } finally {
      setSupportLoading(false);
    }
  };

  const handleRegenerateToken = async () => {
    Modal.confirm({
      title: 'Regenerate Support Token',
      content: 'This will invalidate the current support URL. Any existing links will stop working. Continue?',
      okText: 'Regenerate',
      okType: 'danger',
      onOk: async () => {
        setSupportLoading(true);
        try {
          await axios.post(`/project_board/projects/${id}/regenerate-support-token/`);
          message.success('Support token regenerated');
          fetchProjectData();
        } catch (error) {
          message.error('Failed to regenerate token');
        } finally {
          setSupportLoading(false);
        }
      }
    });
  };

  const handleCopyUrl = () => {
    const supportUrl = `https://support.teson.in/support?code=${project.support_secret_token}`;
    navigator.clipboard.writeText(supportUrl);
    message.success('Support URL copied to clipboard');
  };

  const handleAddComponent = async () => {
    if (!newComponent.trim()) return;

    const updatedComponents = [...(project.support_components || []), newComponent.trim()];
    setSupportLoading(true);
    try {
      await axios.post(`/project_board/projects/${id}/update-support-components/`, {
        components: updatedComponents
      });
      message.success('Component added');
      setNewComponent('');
      setComponentModalVisible(false);
      fetchProjectData();
    } catch (error) {
      message.error('Failed to add component');
    } finally {
      setSupportLoading(false);
    }
  };

  const handleRemoveComponent = async (component) => {
    const updatedComponents = (project.support_components || []).filter(c => c !== component);
    setSupportLoading(true);
    try {
      await axios.post(`/project_board/projects/${id}/update-support-components/`, {
        components: updatedComponents
      });
      message.success('Component removed');
      fetchProjectData();
    } catch (error) {
      message.error('Failed to remove component');
    } finally {
      setSupportLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'PLANNING': 'blue',
      'IN_PROGRESS': 'processing',
      'ON_HOLD': 'warning',
      'COMPLETED': 'success',
      'CANCELLED': 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'PLANNING': 'Planning',
      'IN_PROGRESS': 'In Progress',
      'ON_HOLD': 'On Hold',
      'COMPLETED': 'Completed',
      'CANCELLED': 'Cancelled'
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!project) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Text type="secondary">Project not found</Text>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/projects')}
          >
            Back to Projects
          </Button>
        </Space>
        <Space>
          <Button
            icon={<FolderOutlined />}
            onClick={() => navigate(`/tasks?project_id=${id}`)}
          >
            View Tasks
          </Button>
          {project?.is_owner && (
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => navigate(`/projects/edit/${id}`)}
            >
              Edit Project
            </Button>
          )}
        </Space>
      </div>

      {/* Project Header Card */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={24} align="middle">
          <Col flex="auto">
            <Space direction="vertical" size={4}>
              <Tag color="geekblue" style={{ fontSize: '14px' }}>{project.project_code}</Tag>
              <Title level={2} style={{ margin: 0 }}>{project.name}</Title>
              {project.description && (
                <Text type="secondary" style={{ fontSize: '14px' }}>
                  {project.description}
                </Text>
              )}
            </Space>
          </Col>
          <Col>
            <Space direction="vertical" align="center">
              <Tag color={getStatusColor(project.status)} style={{ fontSize: '14px', padding: '4px 12px' }}>
                {getStatusLabel(project.status)}
              </Tag>
              <Progress
                type="circle"
                percent={project.progress_percentage || 0}
                size={80}
                format={(percent) => `${percent}%`}
              />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {project.completed_tasks || 0}/{project.total_tasks || 0} tasks
              </Text>
            </Space>
          </Col>
        </Row>
      </Card>

      <Row gutter={24}>
        {/* Project Details */}
        <Col xs={24} lg={16}>
          <Card title="Project Details" style={{ marginBottom: '24px' }}>
            <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small">
              <Descriptions.Item label="Project Code">
                <Tag color="geekblue">{project.project_code}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={getStatusColor(project.status)}>
                  {getStatusLabel(project.status)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Owner">
                {project.owner_name ? (
                  <Space>
                    <Avatar size="small" icon={<UserOutlined />} />
                    <span>{project.owner_name}</span>
                  </Space>
                ) : (
                  <Text type="secondary">Not assigned</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Progress">
                <Progress
                  percent={project.progress_percentage || 0}
                  size="small"
                  style={{ width: '150px' }}
                />
              </Descriptions.Item>
              <Descriptions.Item label="Start Date">
                {project.start_date ? (
                  <Space>
                    <CalendarOutlined />
                    {dayjs(project.start_date).format('MMM DD, YYYY')}
                  </Space>
                ) : (
                  <Text type="secondary">Not set</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Due Date">
                {project.due_date ? (
                  <Space>
                    <CalendarOutlined />
                    <Text type={dayjs(project.due_date).isBefore(dayjs()) ? 'danger' : undefined}>
                      {dayjs(project.due_date).format('MMM DD, YYYY')}
                    </Text>
                  </Space>
                ) : (
                  <Text type="secondary">Not set</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Total Tasks" span={2}>
                <Space size="large">
                  <Text>
                    <strong>{project.total_tasks || 0}</strong> Total
                  </Text>
                  <Text type="success">
                    <strong>{project.completed_tasks || 0}</strong> Completed
                  </Text>
                  <Text type="warning">
                    <strong>{(project.total_tasks || 0) - (project.completed_tasks || 0)}</strong> Pending
                  </Text>
                </Space>
              </Descriptions.Item>
            </Descriptions>

            {project.description && (
              <>
                <Divider orientation="left">Description</Divider>
                <Text>{project.description}</Text>
              </>
            )}
          </Card>

          {/* Task Statuses */}
          {taskStatuses.length > 0 && (
            <Card title="Task Statuses" style={{ marginBottom: '24px' }}>
              <Space wrap size={[8, 8]}>
                {taskStatuses.map(status => (
                  <Tag
                    key={status.id}
                    color={status.color}
                    style={{ padding: '4px 12px' }}
                  >
                    {status.name}
                    {status.task_count > 0 && (
                      <span style={{ marginLeft: '8px', fontWeight: 'bold' }}>
                        ({status.task_count})
                      </span>
                    )}
                  </Tag>
                ))}
              </Space>
            </Card>
          )}
        </Col>

        {/* Project Members */}
        <Col xs={24} lg={8}>
          <Card
            title={
              <Space>
                <TeamOutlined />
                <span>Team Members ({members.length})</span>
              </Space>
            }
          >
            {members.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {members.map(member => (
                  <div
                    key={member.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '8px',
                      borderRadius: '8px',
                      backgroundColor: '#fafafa'
                    }}
                  >
                    <Avatar
                      size={40}
                      icon={<UserOutlined />}
                      style={{ backgroundColor: '#1890ff' }}
                    >
                      {member.user_name?.charAt(0)?.toUpperCase()}
                    </Avatar>
                    <div>
                      <Text strong>{member.user_name}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {member.role || 'Member'}
                      </Text>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Text type="secondary">No team members assigned</Text>
            )}
          </Card>

          {/* Quick Actions */}
          <Card title="Quick Actions" style={{ marginTop: '24px' }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button
                block
                icon={<FolderOutlined />}
                onClick={() => navigate(`/tasks?project_id=${id}`)}
              >
                View All Tasks
              </Button>
              {project?.is_owner && (
                <Button
                  block
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/projects/edit/${id}`)}
                >
                  Edit Project
                </Button>
              )}
            </Space>
          </Card>

          {/* Support Settings */}
          <Card
            title={
              <Space>
                <CustomerServiceOutlined />
                <span>Support Portal</span>
              </Space>
            }
            style={{ marginTop: '24px' }}
            loading={supportLoading}
          >
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              {/* Enable/Disable Toggle */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text>Enable Support Tickets</Text>
                <Switch
                  checked={project.is_support_enabled}
                  onChange={handleEnableSupport}
                  loading={supportLoading}
                />
              </div>

              {project.is_support_enabled && (
                <>
                  <Divider style={{ margin: '12px 0' }} />

                  {/* Support URL */}
                  {project.support_secret_token ? (
                    <div>
                      <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                        Support Portal URL
                      </Text>
                      <Alert
                        type="info"
                        showIcon
                        icon={<LinkOutlined />}
                        message={
                          <Text copyable={{ text: `https://support.teson.in/support?code=${project.support_secret_token}` }} style={{ fontSize: '12px', wordBreak: 'break-all' }}>
                            https://support.teson.in/support?code={project.support_secret_token}
                          </Text>
                        }
                        style={{ marginBottom: '8px' }}
                      />
                      <Space>
                        <Button size="small" icon={<CopyOutlined />} onClick={handleCopyUrl}>
                          Copy URL
                        </Button>
                        <Button size="small" icon={<ReloadOutlined />} onClick={handleRegenerateToken} danger>
                          Regenerate
                        </Button>
                      </Space>
                    </div>
                  ) : (
                    <Button type="dashed" block onClick={handleRegenerateToken}>
                      Generate Support Token
                    </Button>
                  )}

                  {/* Public Support Message */}
                  {project.support_public_line && (
                    <>
                      <Divider style={{ margin: '12px 0' }} />
                      <div>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                          Public Support Message
                        </Text>
                        <Alert
                          type="warning"
                          showIcon={false}
                          message={
                            <Text style={{ fontSize: '12px', whiteSpace: 'pre-wrap' }}>
                              {project.support_public_line}
                            </Text>
                          }
                          style={{ marginBottom: '8px' }}
                        />
                      </div>
                    </>
                  )}

                  <Divider style={{ margin: '12px 0' }} />

                  {/* Components */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Bug Report Components
                      </Text>
                      <Button
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={() => setComponentModalVisible(true)}
                      >
                        Add
                      </Button>
                    </div>
                    {project.support_components && project.support_components.length > 0 ? (
                      <Space wrap size={[4, 4]}>
                        {project.support_components.map((comp, idx) => (
                          <Tag
                            key={idx}
                            closable
                            onClose={(e) => {
                              e.preventDefault();
                              handleRemoveComponent(comp);
                            }}
                          >
                            {comp}
                          </Tag>
                        ))}
                      </Space>
                    ) : (
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        No components configured
                      </Text>
                    )}
                  </div>
                </>
              )}

              {!project.is_support_enabled && (
                <Text type="secondary" style={{ fontSize: '12px', textAlign: 'center', display: 'block' }}>
                  Enable support to allow customers to submit bug reports for this project.
                </Text>
              )}
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Add Component Modal */}
      <Modal
        title="Add Support Component"
        open={componentModalVisible}
        onOk={handleAddComponent}
        onCancel={() => {
          setComponentModalVisible(false);
          setNewComponent('');
        }}
        okText="Add"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text type="secondary">
            Components help categorize bug reports (e.g., "Frontend (UI/UX)", "Backend API", "Database", "Authentication")
          </Text>
          <Input
            placeholder="Enter component name"
            value={newComponent}
            onChange={(e) => setNewComponent(e.target.value)}
            onPressEnter={handleAddComponent}
          />
        </Space>
      </Modal>
    </div>
  );
};

export default ProjectView;
